import { useEffect, useState } from 'react'
import { SearchInput } from '../../components/SearchInput'
import { formatArrival, buildLabelFileUrl, externalHttpUrl } from '../../utils/c2cReceiving'
import { OUTCOMES, PHASES, STATUS_PHASES } from './states'
import { OutcomeBadge } from './OutcomeBadge'
import { Pill } from './Pill'
import { RowActionButton } from './RowActionButton'
import { useNamespacedT } from './useNamespacedT'

const T = 'c2c_receiving.scan'

// Focus ring and hover state included: these two links are the manual escape hatch when the
// printer path fails, and on a station PC they are reached with the keyboard.
const LINK_CLASS = 'text-sm text-indigo-600 underline hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-sm'

const Field = ({ label, children, mono = false }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>{children || '-'}</div>
  </div>
)

// Zone A of the Cubbitos screen (spec §5.2): the scan box, the pipeline status line and
// the card for the LAST scanned package. Presentational — every decision lives in the
// container. Enter-submit on purpose: a hand-typed code must not fire partial lookups.
export const ScanStation = ({
  canScan,
  inputRef,
  onScan,
  phase,
  statusError,
  lastResult,
  timeZone,
  busy,
  onLabelAction,
  onCancel,
  cancelAfterMs,
  printFallback,
  onDismissPrintFallback,
}) => {
  const t = useNamespacedT(T)
  const tRoot = useNamespacedT()

  // How long the station has been held. Only shown once it is long enough to be worth
  // acting on: a scan normally settles in a second or two, and a counter on every scan
  // would be noise.
  const [heldForMs, setHeldForMs] = useState(0)
  useEffect(() => {
    if (!busy) {
      setHeldForMs(0)
      return undefined
    }
    const startedAt = Date.now()
    const timer = setInterval(() => setHeldForMs(Date.now() - startedAt), 1000)
    return () => clearInterval(timer)
  }, [busy])
  const canCancel = busy && !!onCancel && heldForMs >= cancelAfterMs

  const isFailure = phase === PHASES.SCAN_FAILED || phase === PHASES.LABEL_FAILED
  // The reason is said once. When the card carries it (a label failure whose card IS the
  // failure) the status line takes the short outcome instead of repeating the sentence; when
  // the card holds something else — a failed scan, or a failed REPRINT whose card still shows
  // the guía that exists — the line is the only place the reason can appear.
  const cardCarriesReason = lastResult?.outcome === OUTCOMES.LABEL_FAILED && !!lastResult?.errorMessage
  const statusText =
    phase === PHASES.CANCELLED
      ? t('cancelled')
      : phase === PHASES.LABEL_FAILED && cardCarriesReason
        ? tRoot(`c2c_receiving.outcome.${OUTCOMES.LABEL_FAILED}`)
        : isFailure
          ? statusError
          : t(`status.${STATUS_PHASES.includes(phase) ? phase : PHASES.IDLE}`)
  const order = lastResult?.order
  const labels = lastResult?.shipping_labels || []
  // One link per label with a file: an order with two packages used to expose only the
  // first, so the second could not be opened by hand when printing failed. Only http(s)
  // urls are rendered — these values come from a carrier payload.
  const labelLinks = labels
    .map(label => ({
      id: label.id ?? label.shipping_number,
      shippingNumber: label.shipping_number,
      href: externalHttpUrl(buildLabelFileUrl(label.label_file_url)),
    }))
    .filter(link => link.href)
  const trackingUrl = externalHttpUrl(labels[0]?.tracking_url)

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-4 mb-4">
      {canScan ? (
        // SearchInput renders a bare `<input name="search">` and forwards no aria-label,
        // so the screen's primary control is named by wrapping it: an implicit <label>
        // needs nothing from the shared component.
        <label className="block">
          <span className="sr-only">{t('placeholder')}</span>
          <SearchInput ref={inputRef} inputValue="" hideIcons disabled={busy} onSubmit={onScan} placeholder={t('placeholder')} className="w-full" />
        </label>
      ) : (
        <div className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">{t('read_only')}</div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-3">
        <div className={`text-sm font-medium ${isFailure ? 'text-red-600' : 'text-gray-600'}`} role="status">
          {statusText}
        </div>
        {canCancel && (
          <>
            <span className="text-xs text-gray-500 tabular-nums">{Math.floor(heldForMs / 1000)}s</span>
            <button type="button" className={LINK_CLASS} onClick={onCancel}>
              {t('actions.cancel')}
            </button>
          </>
        )}
      </div>

      {printFallback && (
        <div className="mt-3 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700" role="alert">
          <span>{printFallback === 'blocked' ? t('print_blocked') : t('print_fallback')}</span>
          <button type="button" className="underline font-medium" onClick={onDismissPrintFallback}>
            {t('dismiss')}
          </button>
        </div>
      )}

      {order ? (
        <div className="mt-4 rounded-md border border-gray-200 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-2xl font-semibold text-gray-900">{order.receiver_name || '-'}</div>
              <div className="text-sm text-gray-600">
                {order.destination_point_name || '-'}
                {order.destination_carrier_name ? ` · ${order.destination_carrier_name}` : ''}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {lastResult.scanReplayed && <Pill tone="border-amber-200 bg-amber-100 text-amber-800">{t('scan_replayed')}</Pill>}
              <OutcomeBadge outcome={lastResult.outcome} />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Field label={t('fields.roll_code')} mono>
              {order.roll_code}
            </Field>
            <Field label={t('fields.order_number')}>{order.order_number}</Field>
            <Field label={t('fields.arrived_at')}>{formatArrival(order.arrived_at_warehouse_at, timeZone)}</Field>
            <Field label={t('fields.shipping_number')} mono>
              {labels.length === 0 ? (
                '-'
              ) : (
                <>
                  {labels.map(label => label.shipping_number).join(', ')}
                  {!labels.some(label => label.label_file_url) && (
                    <span className="ml-1 font-sans text-xs font-normal text-gray-400">{t('fields.no_file')}</span>
                  )}
                </>
              )}
            </Field>
          </div>

          {lastResult.outcome === OUTCOMES.LABEL_FAILED && lastResult.errorMessage && (
            <div className="mt-3 text-sm text-red-600">{lastResult.errorMessage}</div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Same gate as the queue rows and the strip: without c2c_receiving_scan the
                endpoint answers 403, so the button must not look live (a permission change
                mid-session is enough to reach this with a card on screen). */}
            <RowActionButton
              order={order}
              label={lastResult.outcome === OUTCOMES.LABEL_FAILED ? t('actions.retry_label') : t('actions.reprint')}
              type={lastResult.outcome === OUTCOMES.LABEL_FAILED ? 'primary' : 'secondary'}
              canScan={canScan}
              busy={busy}
              dense={false}
              onClick={onLabelAction}
            />
            {trackingUrl && (
              <a className={LINK_CLASS} href={trackingUrl} target="_blank" rel="noopener noreferrer">
                {t('fields.tracking')}
              </a>
            )}
            {labelLinks.map(link => (
              <a key={link.id} className={LINK_CLASS} href={link.href} target="_blank" rel="noopener noreferrer">
                {labelLinks.length > 1 ? `${t('fields.open_label')} · ${link.shippingNumber}` : t('fields.open_label')}
              </a>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4 text-sm text-gray-400">{t('no_result')}</div>
      )}
    </div>
  )
}
